import rgba from 'polished/lib/color/rgba';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { ConfirmationOverlay } from '../../../../app/common/components/ConfirmationOverlay';
import Icon from '../../../../app/common/components/Icon';
import RadioButton from '../../../../app/common/components/RadioButton';
import { TooltipWrapper } from '../../../../app/common/components/TooltipWrapper';
import ICONS from '../../../../app/common/constants/icons';
import { FONT_FAMILY, MQS, SHADOWS } from '../../../../app/common/constants/styles';
import BlockUi from '../../../../declarations/js-modules/react-block-ui.d';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { TextHelpers } from '../../../../utils/textHelpers';
import { getCardBrandFromAPIName } from '../cardBrands/cardBrandHelpers';
import { cardBrandsIcons } from '../cardBrands/cardBrandsIcons.constant';
import { ICard, ICardPutRequest } from './card.interface';
import { CardFormatterService } from './cardFormatter.service';
import { getChallengeData } from '../../../../utils/challengerMain';
import { InjectedIntlProps } from 'react-intl';
import { addRatEvent } from '../../../../utils/ratClickEvents';
import { RegionHelper } from '../../../../utils/RegionHelper';

interface IColorsProps {
    background: string;
    color: string;
}

export interface ICardOwnProps {
    card: ICard;
    onEdit: (card: ICard) => any;
    isOnlyCard?: boolean;
    qaId?: string;
    showBillingAddress: boolean;
}

export interface ICardStateProps {
    billingAddress: ReactNode;
    cardRestrictionEnabled: boolean;
}

export interface ICardDispatchProps {
    deleteCard: (cardToken: string) => void;
    setIsPrimary: (cardToken: string, updatedCard: ICardPutRequest) => void;
    updateCard: (cardToken: string, updatedCard: ICardPutRequest) => void;
    challengerPid?: string;
}

export type CardProps = ICardOwnProps & ICardStateProps & ICardDispatchProps;

export interface ICardState {
    deleteConfirmationOpen?: boolean;
    deleteLoading?: boolean;
}

const CardWrapper = styled.div`
    background: ${(props: IColorsProps) => props.background};
    border-radius: 3px;
    box-shadow: ${SHADOWS.level1};
    color: ${(props: IColorsProps) => props.color};
    height: 190px;
    font-size: 1rem;
    max-width: 330px;
    padding: 25px;
    padding-right: 0;
    position: relative;
    width: 95vw;

    @media (min-width: ${MQS.mq4Max}) {
        font-size: 1rem;
    }
`;

const ControlButtons = styled.div`
    display: flex;
    padding-right: 25px;
`;

const RadioButtonWrapper = styled.div`
    & > button {
        color: ${(props: { color: string; checked?: boolean }) =>
            props.checked ? props.color : rgba(props.color, 0.7)};
        border: none;
        background-color: ${rgba('#ffffff', 0.2)};

        input:checked ~ .radioBox,
        & > div:checked {
            background-color: ${(props: { color: string }) => rgba(props.color, 0.2)};
            border-color: ${(props: { color: string }) => props.color};

            &::after {
                background: ${(props: { color: string }) => props.color};
            }
        }

        & > div {
            border-color: ${(props: { color: string }) => rgba(props.color, 0.6)};
        }
    }
`;

const ButtonInline = styled.button`
    border: none;
    background-color: transparent;
    cursor: pointer;
    color: ${(props: { color: string }) => props.color};
    opacity: 0.7;
`;

const BillingAddress = styled.div`
    color: ${(props: { color: string }) => props.color};
    font-size: 0.5rem;
    line-height: 1;
    max-width: 150px;
    opacity: 0.7;
    position: absolute;
    right: 25px;
    top: 20px;
    text-align: right;

    span:first-child {
        font-weight: 500;
        text-transform: uppercase;
    }

    p {
        line-height: 1;
        margin-top: 0;
    }
`;

const CardNumber = styled.div`
    font-size: 0.875rem;
    font-weight: 500;
    margin-top: 45px;

    @media (min-width: ${MQS.mq2Min}) {
        font-size: 1.125rem;
    }
`;

const CardHolderName = styled.div`
    font-size: 0.875rem;
    overflow: hidden;
    max-height: 20px;
    max-width: 80%;
    padding-right: 10px;
    word-break: break-all;

    @media (min-width: ${MQS.mq2Min}) {
        font-size: 1rem;
    }
`;
const CardNumberTitle = styled.span`
    margin-right: 10px;
`;

const CardNumberValue = styled.span``;

const CardExpiryDate = styled.div`
    font-size: 0.875rem;
    max-height: 18px;
    width: 60px;

    @media (min-width: ${MQS.mq2Min}) {
        font-size: 1rem;
    }
`;

const Logo = styled.div`
    background: url('${(props: { backgroundUrl: string }) => props.backgroundUrl}');
    background-position: center left;
    background-repeat: no-repeat;
    background-size: contain;
    left: 25px;
    position: absolute;
    top: 20px;

    &.card-logo-amex {
        height: 22%;
        width: 20%;
    }

    &.card-logo-diners-club {
        height: 15%;
        width: 50%;
    }

    &.card-logo-default {
        height: 15%;
        width: 35%;
    }

    &.card-logo-visa {
        height: 15%;
        width: 20%;
    }

    &.card-logo-maestro {
        height: 22%;
        width: 15%;
    }

    &.card-logo-mastercard {
        height: 20%;
        width: 45%;
    }

    &.card-logo-unionpay {
        height: 20%;
        width: 45%;
    }
`;

const CardNameDateWrapper = styled.div`
    display: flex;
    padding-right: 25px;
`;

const Spacer = styled.div`
    flex: 1 1 0px;
`;

const Separator = styled.hr`
    background: ${(props: { color: string }) => props.color};
    border: none;
    height: 1px;
    margin: 15px 0;
    opacity: 0.1;
`;

const ConfirmationWarningText = styled.div`
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    margin: 10px 0;

    p:first-child {
        margin-top: 0;
    }
`;

const DisabledCardWrapper = styled(BlockUi)`
    .block-ui-overlay {
        background: transparent !important;
        cursor: not-allowed !important;
    }
`;

export default class Card extends React.Component<CardProps & InjectedIntlProps, ICardState> {
    constructor(props: CardProps & InjectedIntlProps) {
        super(props);

        this.state = {
            deleteConfirmationOpen: false,
            deleteLoading: false,
        };

        this.onAcceptDelete = this.onAcceptDelete.bind(this);
        this.onConfirmationClose = this.onConfirmationClose.bind(this);
        this.onCancelDelete = this.onCancelDelete.bind(this);
    }

    public render() {
        const { deleteConfirmationOpen } = this.state;

        return (
            <div>
                {deleteConfirmationOpen && this.deleteConfirmationOverlay}
                {this.card}
            </div>
        );
    }

    componentDidUpdate() {
        const blockContainer = document.getElementsByClassName(
            'block-ui-container',
        )[0] as HTMLElement;
        const confirmButton = document.getElementById('confirm') as HTMLElement;
        if (blockContainer) {
            blockContainer.addEventListener('focus', (event) => {
                event.preventDefault();
                confirmButton.focus();
            });
        }
    }

    private get card() {
        const {
            billingAddress,
            card,
            onEdit,
            setIsPrimary,
            qaId,
            showBillingAddress,
            challengerPid,
            intl,
        } = this.props;

        const cardBrand = getCardBrandFromAPIName(card.brandName);
        const backgroundColor = cardBrand ? cardBrand.background : '#666666';
        const color = cardBrand ? cardBrand.color : '#ffffff';
        const cardNumber = CardFormatterService.getLast4Digits(`************${card.numberLast}`);

        const onDeleteClick = () => {
            addRatEvent('payment_method', 'delete_card');
            this.setState({ deleteConfirmationOpen: true });
        };

        const onEditClick = () => {
            addRatEvent('payment_method', 'edit_card');
            onEdit(card);
        };

        const onSetDefault = (value: boolean) => {
            addRatEvent('payment_method', 'set_default_card_in_list_view');
            const newCard: ICardPutRequest = {
                ...card,
                isPrimary: value,
            };

            if (challengerPid) {
                newCard.challengeData = getChallengeData(challengerPid);
            }

            setIsPrimary(card.cardToken, newCard);
        };

        return (
            <CardWrapper
                className={`card-${cardBrand ? cardBrand.cssId : 'default'}`}
                background={backgroundColor}
                color={color}
                data-qa-id={`card-${qaId}`}
            >
                <Logo
                    className={`card-logo-${cardBrand ? cardBrand.cssId : 'default'}`}
                    backgroundUrl={
                        cardBrand
                            ? cardBrandsIcons[cardBrand.logoId]
                            : cardBrandsIcons['default-logo']
                    }
                    tabIndex={0}
                    role="img"
                    aria-label={cardBrand.apiName}
                />
                {/* Only render when feature is enabled (cards.billingAddress) AND the card has a resolved billing address */}
                {showBillingAddress && billingAddress && (
                    <BillingAddress color={color}>
                        {TranslationHelpers.getFormattedMessage('address.billing')}
                        {billingAddress}
                    </BillingAddress>
                )}
                <CardNumber data-qa-id="card-number" tabIndex={0}>
                    <CardNumberTitle>
                        {TranslationHelpers.getFormattedMessage('payment.card_number')}
                    </CardNumberTitle>
                    <CardNumberValue>{cardNumber}</CardNumberValue>
                </CardNumber>
                <CardNameDateWrapper>
                    <CardHolderName data-qa-id="card-holder-name" className="notranslate">
                        {TextHelpers.trimText(card.ownerName, 18)}
                    </CardHolderName>
                    <Spacer />
                    <CardExpiryDate data-qa-id="card-expiry-date">
                        {`${card.expireMonth} / ${card.expireYear.slice(2, 4)}`}
                    </CardExpiryDate>
                </CardNameDateWrapper>
                <Separator color={color} />
                <ControlButtons color={color}>
                    <TooltipWrapper
                        id={`set-is-primary-${card.cardToken}`}
                        tooltipText={TranslationHelpers.getMessageDescriptor(
                            card.isPrimary
                                ? 'tooltip.default_card.activated'
                                : 'tooltip.default_card.deactivated',
                        )}
                    >
                        <RadioButtonWrapper color={color} checked={card.isPrimary}>
                            <RadioButton
                                id="isShipping"
                                label={TranslationHelpers.getMessageDescriptor('payment.default')}
                                value={card.isPrimary}
                                onCheck={() => onSetDefault(true)}
                                qaId="is-default"
                            />
                        </RadioButtonWrapper>
                    </TooltipWrapper>
                    <Spacer />
                    <ButtonInline
                        color={color}
                        title="Edit"
                        onClick={onEditClick}
                        data-qa-id="edit-card"
                        aria-label={intl.formatMessage(
                            TranslationHelpers.getMessageDescriptor('button.edit'),
                        )}
                    >
                        <Icon size="20px" icon={ICONS.EDIT} />
                    </ButtonInline>
                    <ButtonInline
                        color={color}
                        title="Delete"
                        onClick={onDeleteClick}
                        data-qa-id="delete-card"
                        aria-label={intl.formatMessage(
                            TranslationHelpers.getMessageDescriptor('button.delete'),
                        )}
                    >
                        <Icon size="20px" icon={ICONS.TRUSH} />
                    </ButtonInline>
                </ControlButtons>
            </CardWrapper>
        );
    }

    private get deleteConfirmationOverlay() {
        const { card, isOnlyCard, cardRestrictionEnabled } = this.props;
        const { deleteConfirmationOpen } = this.state;

        if (!deleteConfirmationOpen) {
            return '';
        }
        // Show card sharing disclaimer only for non-JP regions, unless disabled via CARD_RESTRICTION_ENABLED
        const showCardSharingDisclaimer = !RegionHelper.isJP() && cardRestrictionEnabled;

        const warningTextTop = card.isPrimary
            ? TranslationHelpers.getFormattedMessage(
                  'payment.delete_confirmation.warning_top_default',
              )
            : TranslationHelpers.getFormattedMessage('payment.delete_confirmation.warning_top');

        const warningTextBottom = card.isPrimary
            ? TranslationHelpers.getFormattedMessage(
                  'payment.delete_confirmation.warning_bottom_default',
              )
            : '';

        const disclaimerText = TranslationHelpers.getFormattedMessage(
            'payment.delete_confirmation.disclaimer',
        );

        const disclaimerTextSubscription = TranslationHelpers.getFormattedMessage(
            'payment.delete_confirmation.disclaimer_subscription',
        );

        return (
            <ConfirmationOverlay
                title={TranslationHelpers.getMessageDescriptor('payment.delete')}
                isOpen={deleteConfirmationOpen}
                onAccept={this.onAcceptDelete}
                onCancel={this.onCancelDelete}
                onClose={this.onConfirmationClose}
                confirmButtonText={TranslationHelpers.getMessageDescriptor('button.delete')}
            >
                <ConfirmationWarningText>
                    <p>
                        {warningTextTop}
                        {showCardSharingDisclaimer && <> {disclaimerText}</>}
                    </p>
                    <p>{showCardSharingDisclaimer && disclaimerTextSubscription}</p>
                </ConfirmationWarningText>
                <DisabledCardWrapper blocking={true}>{this.card}</DisabledCardWrapper>
                <ConfirmationWarningText>
                    <p>{isOnlyCard ? '' : warningTextBottom}</p>
                </ConfirmationWarningText>
            </ConfirmationOverlay>
        );
    }

    private onConfirmationClose() {
        this.setState({ deleteConfirmationOpen: false });
    }

    private onAcceptDelete() {
        const { card, deleteCard } = this.props;
        addRatEvent('payment_method', 'delete_card_confirm');
        deleteCard(card.cardToken);
        this.setState({ deleteConfirmationOpen: false });
    }

    private onCancelDelete() {
        this.setState({ deleteConfirmationOpen: false });
    }
}
